<template>
  <panel
    ref="panel"
    panel-class="scroll-horizontally scroll-vertically shadows"
  >
    <table ref="configurator" class="table-field table-field-configurator">
      <thead>
        <tr>
          <th></th>
          <th :colspan="columns.length">
            <div class="columns">
              <columns
                :draggable-columns="columns"
                :input-types="inputTypes"
                :supported-languages="supportedLanguages"
                :language="language"
                :labels="labels"
                @dragging="setColumns"
              >
              </columns>
            </div>
          </th>
          <th class="add-column">
            <a
              @click.prevent="
                insertColumn({ position: 'right', from: lastColumn })
              "
            >
              <span class="af-icons af-icons-plus"></span>
            </a>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, rowIndex) in rows" :key="row">
          <td v-if="rowIndex === 0" :rowspan="rows.length">
            <div class="rows">
              <rows
                :draggable-rows="rows"
                :active-row="activeRow"
                @dragging="setRows"
              >
              </rows>
            </div>
          </td>
          <cell
            v-for="(column, columnIndex) in columns"
            :key="row + ':' + column"
            :column="column"
            :column-index="columnIndex"
            :row="row"
            :row-index="rowIndex"
            :last-column-index="columns.length - 1"
            :active-row.sync="activeRow"
            :supported-languages="supportedLanguages"
            :language="language"
            :labels="labels"
            @editor-opened="expandPanel"
            @editor-closed="collapsePanel"
          >
          </cell>
          <td @mouseenter="activeRow = row" @mouseleave="activeRow = ''">
            <a
              v-if="canDeleteRows && activeRow === row"
              class="delete-row"
              @click.prevent="removeRow(row)"
            >
              <span class="af-icons af-icons-close-circle"></span>
            </a>
          </td>
        </tr>
      </tbody>
      <tfoot>
        <tr>
          <td></td>
          <td :colspan="columns.length">
            <a
              class="btn btn-tertiary btn-xs add-row"
              @click.prevent="addRow"
              >{{ labels.addRow }}</a
            >
            <label class="dynamic-rows checkbox-inline">
              <input
                type="checkbox"
                :checked="dynamicRowsEnabled"
                @change="handleDynamicRows"
              />
              {{ labels.dynamicRows }}
            </label>
          </td>
          <td></td>
        </tr>
      </tfoot>
    </table>
    <input
      v-model="tableFieldConfiguration"
      type="hidden"
      name="configuration"
    />
    <template v-for="(translation, language) in translations">
      <input
        v-for="(value, field) in translation"
        v-if="supportedLanguages.includes(language)"
        :key="field + '-' + language"
        type="hidden"
        :name="'translatedConfiguration[' + field + '][' + language + ']'"
        :value="value"
      />
    </template>
  </panel>
</template>

<script>
import Vue from 'vue';
import { Panel } from 'vue-bootstrap';
import Columns from './TableFieldConfiguratorColumns.vue';
import Rows from './TableFieldConfiguratorRows.vue';
import Cell from './TableFieldConfiguratorCell.vue';
import { mapGetters, mapMutations, mapState } from 'vuex';
import { createStore } from '../../store/modules/table-field';

export default {
  components: {
    Panel,
    Columns,
    Rows,
    Cell
  },
  props: {
    configuration: {
      type: Object,
      default: () => ({})
    },
    configurationTranslated: {
      type: Object,
      default: () => ({})
    },
    inputTypes: {
      type: Array,
      default: () => [
        { id: 'label', name: 'Row label' },
        { id: 'text', name: 'Plain text' },
        { id: 'integer', name: 'Integer (1)' },
        { id: 'decimal', name: 'Decimal (0.1)' },
        { id: 'decimal-precise', name: 'Decimal (0.01)' },
        { id: 'currency', name: 'Currency' }
      ]
    },
    supportedLanguages: {
      type: Array,
      default: () => ['en_GB']
    },
    language: {
      type: String,
      default: 'en_GB'
    },
    labels: {
      type: Object,
      default: () => ({
        addRow: 'Add row',
        dynamicRows: 'User can add additional rows',
        configureColumn: 'Configure column',
        insertLeft: 'Insert left',
        insertRight: 'Insert right',
        deleteColumn: 'Delete column',
        column: 'Column',
        columnLabel: 'Column label',
        columnType: 'Column type',
        rowLabel: 'Row label',
        save: 'Save',
        cancel: 'Cancel'
      })
    }
  },
  data() {
    return {
      activeRow: ''
    };
  },
  computed: {
    ...mapState([
      'columns',
      'rows',
      'filters',
      'dynamicRowsEnabled',
      'translations'
    ]),
    ...mapGetters(['getConfiguration']),
    tableFieldConfiguration() {
      return JSON.stringify(this.getConfiguration());
    },
    tableFieldTranslations() {
      return JSON.stringify(this.translations);
    },
    canDeleteRows() {
      return this.rows.length > 1;
    },
    lastColumn() {
      return this.columns[this.columns.length - 1];
    }
  },
  watch: {
    tableFieldConfiguration() {
      this.$emit(
        'updated',
        this.tableFieldConfiguration,
        this.tableFieldTranslations
      );
    },
    tableFieldTranslations() {
      this.$emit(
        'updated',
        this.tableFieldConfiguration,
        this.tableFieldTranslations
      );
    }
  },
  beforeCreate() {
    this.$store = createStore();
  },
  created() {
    this.setConfiguration(this.configuration);
    this.setTranslations(this.configurationTranslated);
  },
  methods: {
    ...mapMutations([
      'setConfiguration',
      'setColumns',
      'setRows',
      'enableDynamicRows',
      'setTranslations',
      'addRow',
      'deleteRow',
      'insertColumn'
    ]),
    handleDynamicRows(event) {
      this.enableDynamicRows(event.target.checked);
    },
    expandPanel() {
      Vue.nextTick(() => {
        setTimeout(() => {
          const configurator = this.$refs.configurator;
          const panel = this.$refs.panel;

          if (!configurator || !panel) {
            return;
          }

          const configuratorHeight = configurator.clientHeight + 35;
          const panelHeight = panel.$el.scrollHeight;

          if (panelHeight > configuratorHeight) {
            configurator.style.marginBottom =
              panelHeight - configuratorHeight + 30 + 'px';
          }
        }, 100);
      });
    },
    collapsePanel() {
      Vue.nextTick(() => {
        const configurator = this.$refs.configurator;

        if (configurator) {
          this.$refs.configurator.style.marginBottom = 0;
        }
      });
    },
    removeRow(row) {
      // Make sure the clickOutside directive doesn't incorrectly detect
      // a click outside when the row disappears
      setTimeout(() => this.deleteRow(row), 0);
    }
  }
};
</script>
